import { FlowExtensionParamWidgetTypes } from '../widget-types'
import { type AnyZod, inspect } from './introspect'

// What a declaration explains about itself when the author wrote no `.describe()` — so `z.email()` stays a
// legal one-liner and still renders with usable help text.
//
// These are applied at RENDER time, not baked into the published definition: the console derives them from
// the schema the definition carries, so rewording an entry here reaches every already-deployed service
// without a redeploy. `field-mapper` therefore publishes the author's description or nothing at all.
//
// Also read by ./messages.ts, which restates a format failure using the same wording the field advertises.
//
// Labels are NOT derived this way at all — see ./labels.ts for why the field key always wins.

// Three maps rather than one, because the three key spaces overlap: `date` is both a zod format
// (`z.iso.date()`) and a core type (`z.date()`). Kept separate, each stays a flat literal table and a
// collision is impossible.

/** `z.text()` — FlowRunner's own marker, the most specific thing a field can declare. */
export const WIDGET_DESCRIPTIONS: Record<string, string> = {
  [FlowExtensionParamWidgetTypes.MULTI_LINE_TEXT]: 'Expects text; line breaks are allowed',
}

/**
 * `z.json()`. More specific than the widget it carries: it renders as the same textarea `z.text()` does, so
 * the widget wording would say "line breaks are allowed" and leave out the only rule that actually rejects
 * anything — that what is typed has to parse.
 */
export const JSON_DESCRIPTION = 'Expects JSON, e.g. {"key": "value"}'

/**
 * The plain core type, when nothing more specific is declared.
 *
 * Only worth filling in where the widget does NOT already convey the type. `z.number()` earns one because a
 * number renders as a plain text field — there is no numeric widget — so "Expects a number" is real
 * information. `z.string()` and `z.boolean()` do not: the field is visibly a text box or a toggle, and
 * filling them would put generic prose under every field of every service, overwriting the meaningful empty
 * state that says "the service author documented this".
 */
export const TYPE_DESCRIPTIONS: Record<string, string> = {
  number : 'Expects a number',
  bigint : 'Expects a whole number',
  literal: 'Expects one fixed value',
  enum   : 'Expects one of the listed options',
  // `z.date()` is deferred until the DATE type gets a widget; `z.iso.date()` is the working alternative.
  date   : 'Expects a date',
  // string / boolean / array / object are deliberately absent — see above.
}

/**
 * A zod `format` token. Spelled out rather than composed — the wording is what a customer reads, so it
 * should be editable here without reasoning about how it is assembled. Every example is a value that
 * actually satisfies its helper.
 */
export const FORMAT_DESCRIPTIONS: Record<string, string> = {
  // ── web ─────────────────────────────────────────────────────────────────────
  url      : 'Expects a URL, e.g. https://example.com',
  email    : 'Expects an email address, e.g. foo@bar.com',
  hostname : 'Expects a hostname, e.g. api.example.com',
  e164     : 'Expects a phone number in E.164 form, e.g. +14155552671',
  emoji    : 'Expects an emoji, e.g. 🎉',
  // ── identifiers ─────────────────────────────────────────────────────────────
  uuid     : 'Expects a UUID, e.g. 3f2504e0-4f89-11d3-9a0c-0305e82c3301',
  guid     : 'Expects a GUID, e.g. 3f2504e0-4f89-11d3-9a0c-0305e82c3301',
  nanoid   : 'Expects a Nano ID, e.g. V1StGXR8_Z5jdHi6B-myT',
  cuid     : 'Expects a CUID, e.g. cjld2cyuq0000t3rmniod1foy',
  cuid2    : 'Expects a CUID2, e.g. tz4a98xxat96iws9zmbrgj3a',
  ulid     : 'Expects a ULID, e.g. 01ARZ3NDEKTSV4RRFFQ69G5FAV',
  xid      : 'Expects an XID, e.g. 9m4e2mr0ui3e8a215n4g',
  ksuid    : 'Expects a KSUID, e.g. 0ujsswThIGTUYm2K8FjOOfXtY1K',
  // ── ISO 8601 (z.iso.*) — a validated STRING, unlike the deferred z.date() ───
  datetime : 'Expects an ISO date and time, e.g. 2024-01-01T00:00:00Z',
  date     : 'Expects an ISO date, e.g. 2024-01-01',
  time     : 'Expects an ISO time, e.g. 12:30:00',
  duration : 'Expects an ISO duration, e.g. P3Y6M4D',
  // ── encodings ───────────────────────────────────────────────────────────────
  base64   : 'Expects a base64 string, e.g. aGVsbG8=',
  base64url: 'Expects a base64url string, e.g. aGVsbG8',
  hex      : 'Expects a hex string, e.g. deadbeef',
  jwt      : 'Expects a JWT, e.g. eyJhbGciOiJIUzI1NiJ9.eyJhIjoxfQ.c2ln',
  // ── network ─────────────────────────────────────────────────────────────────
  ipv4     : 'Expects an IPv4 address, e.g. 192.168.0.1',
  ipv6     : 'Expects an IPv6 address, e.g. 2001:db8::1',
  cidrv4   : 'Expects an IPv4 range, e.g. 192.168.0.0/24',
  cidrv6   : 'Expects an IPv6 range, e.g. 2001:db8::/32',
}

/**
 * What a field's declaration explains about itself, most specific wins:
 *
 *   1. `z.json()`                 — the content rule, which outranks the widget it shares with `z.text()`
 *   2. a FlowRunner widget marker — `z.text()`, a string the console renders specially
 *   3. a zod `format` token       — `z.email()`, `z.ipv4()`, `z.iso.date()`, …
 *   4. the plain core type        — `number`, `bigint`, `literal`, `enum`, `date`
 *
 * All three are read past the optional/nullable/default wrappers, so a modifier never changes the answer.
 */
export function describeField(schema: AnyZod): string | undefined {
  const ins = inspect(schema)
  const format = (ins.core as { _zod?: { def?: { format?: string } } } | undefined)?._zod?.def?.format

  if (ins.meta.frJson) {
    return JSON_DESCRIPTION
  }

  return WIDGET_DESCRIPTIONS[ins.meta.frWidget ?? ''] ??
    FORMAT_DESCRIPTIONS[format ?? ''] ??
    TYPE_DESCRIPTIONS[ins.coreType]
}
